/**
 * Catálogo de CFDIs descargados del SAT (Punto 1 del feedback ASI).
 *
 * Mapeo backend:
 *   GET  /cfdi-catalogo/{rfc}                     -> a2c-client-asiContadores/src/lambdas/cfdi_catalogo
 *   POST /cfdi-catalogo/{rfc}/export               -> cfdi_catalogo_export_iniciar (crea job async)
 *   GET  /cfdi-catalogo/{rfc}/export/status/{job_id} -> cfdi_catalogo_export_status (polling)
 *   GET  /cfdi-catalogo/{rfc}/pdf/{uuid}           -> cfdi_catalogo_pdf (síncrono, PDF on-demand)
 */
import { apiClient } from './clienteHttp';
import type { CfdiCatalogoExportJobStatus, CfdiCatalogoFiltros, CfdiCatalogoResponse } from './types';

/** `incluirPaginacion` en false omite page/page_size — el endpoint de export
 * no los acepta (es unpaginado, siempre trae el dataset filtrado completo). */
function filtrosAQueryParams(filtros: CfdiCatalogoFiltros, incluirPaginacion = true): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (filtros.tipo_doc)  params.tipo_doc  = filtros.tipo_doc;
  if (filtros.direccion) params.direccion = filtros.direccion;
  if (filtros.folio)     params.folio     = filtros.folio;
  if (filtros.uuid)      params.uuid      = filtros.uuid;
  if (filtros.desde)     params.desde     = filtros.desde;
  if (filtros.hasta)     params.hasta     = filtros.hasta;
  if (filtros.rfc)       params.rfc       = filtros.rfc;
  if (filtros.monto_min != null) params.monto_min = filtros.monto_min;
  if (filtros.monto_max != null) params.monto_max = filtros.monto_max;
  if (incluirPaginacion) {
    if (filtros.page != null)      params.page      = filtros.page;
    if (filtros.page_size != null) params.page_size = filtros.page_size;
  }
  return params;
}

export async function getCfdiCatalogo(rfc: string, filtros: CfdiCatalogoFiltros = {}): Promise<CfdiCatalogoResponse> {
  const { data } = await apiClient.get<CfdiCatalogoResponse>(`/cfdi-catalogo/${encodeURIComponent(rfc)}`, {
    params: filtrosAQueryParams(filtros),
  });
  return data;
}

/** Excel del catálogo — se genera en el backend (ver cfdi_catalogo_export_iniciar)
 * porque las columnas configurables ("Editar Columnas") pueden requerir
 * re-parsear XML de S3, algo que ya no cabe en memoria/tiempo del navegador
 * ni en el cap de 29s de una integración síncrona de API Gateway REST. Es
 * async (POST inicia un job + GET status hace polling), mismo patrón que
 * iniciarReporteDownloadFiltrado/getReporteDownloadFiltradoStatus. La
 * selección de columnas no viaja acá — el backend la resuelve por el
 * usuario autenticado (GET/PUT /preferencias/columnas-excel). */
export async function iniciarCfdiCatalogoExport(rfc: string, filtros: CfdiCatalogoFiltros = {}): Promise<{ job_id: string }> {
  const { data } = await apiClient.post<{ job_id: string; estado: string }>(
    `/cfdi-catalogo/${encodeURIComponent(rfc)}/export`,
    null,
    { params: filtrosAQueryParams(filtros, false) },
  );
  return data;
}

export async function getCfdiCatalogoExportStatus(rfc: string, jobId: string): Promise<CfdiCatalogoExportJobStatus> {
  const { data } = await apiClient.get<CfdiCatalogoExportJobStatus>(
    `/cfdi-catalogo/${encodeURIComponent(rfc)}/export/status/${encodeURIComponent(jobId)}`,
  );
  return data;
}

/** PDF (representación impresa) on-demand de un CFDI del catálogo — ver
 * src/lambdas/cfdi_catalogo_pdf/. Genera el PDF la primera vez (o lo reusa
 * si ya se generó antes) y devuelve un presigned URL de S3 válido 7 días. */
export async function getCfdiCatalogoPdfUrl(rfc: string, uuidCfdi: string): Promise<{ url: string }> {
  const { data } = await apiClient.get<{ url: string }>(
    `/cfdi-catalogo/${encodeURIComponent(rfc)}/pdf/${encodeURIComponent(uuidCfdi)}`,
  );
  return data;
}
